import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Alert, Empty, Input, Select, Spin, Tag, Tooltip } from 'antd';
import { DownloadOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { CSVLink } from 'react-csv';
import { useLazyQuery } from 'react-apollo';

import Button from '../../../shared/globalStyling/Button';
import PaginationTable from '../../../shared/PaginationTable';
import { useBreakpoints } from '../../../shared/globalStyling/screenSizes';
import { pagination } from '../../../ux/Tasks/configs/designTokens';
import GET_GEO_LIBRARY from '../../../../core/GraphQl/Queries/GET_GEO_LIBRARY';
import { determineUserAccess } from '../../../../core/permissions/userLevelAccess';

const { Option } = Select;

// Columns mirror the CSV export so what's on screen is what lands in the file.
const CSV_HEADERS = [
  { label: 'Type', key: 'resourceType' },
  { label: 'Nexxen ID', key: 'id' },
  { label: 'Name', key: 'name' },
  { label: 'Display Name', key: 'displayName' },
  { label: 'DMA Code', key: 'dmaCode' },
  { label: 'Region ID', key: 'regionId' },
  { label: 'State', key: 'regionName' },
  { label: 'State Code', key: 'regionCode' },
  { label: 'Country ID', key: 'countryId' },
  { label: 'Country', key: 'countryName' },
  { label: 'Country Code', key: 'countryCode' },
];

const TYPE_COLORS = { REGION: 'blue', DMA: 'purple', CITY: 'default' };

// external-service caps a page at 1000 rows; ask for the maximum so the default
// region+dma view (~270) always arrives whole.
const PAGE_LIMIT = 1000;

const GeoLibrary = ({ toolbarContainer, currentuser }) => {
  const [types, setTypes] = useState(['REGION', 'DMA']);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pagination.defaultPageSize);

  const { isTablet } = useBreakpoints();

  // forceRefresh forces a live pull against Nexxen's rate-limited API, so the
  // resolver rejects it for non-admins. Hide the control rather than let it throw.
  const isAdmin = Boolean(
    currentuser && currentuser.permission && determineUserAccess(currentuser.permission).isAdmin
  );

  const [fetchGeoLibrary, { data, loading, error }] = useLazyQuery(GET_GEO_LIBRARY, {
    fetchPolicy: 'network-only',
  });

  // Cities are opt-in on the backend because enumerating them costs one paged
  // Nexxen call per state, so only ask for what the user selected.
  const loadLibrary = (forceRefresh = false) => {
    fetchGeoLibrary({
      variables: { country: 'US', types, limit: PAGE_LIMIT, forceRefresh },
    });
  };

  useEffect(() => {
    loadLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [types]);

  const library = data && data.geoLibrary ? data.geoLibrary : null;
  const entries = library ? library.entries : [];

  // Filter client-side so typing doesn't re-hit Nexxen — the whole snapshot is
  // already in memory.
  const filteredData = useMemo(() => {
    if (!searchText) return entries;
    const needle = searchText.toLowerCase();
    return entries.filter((entry) =>
      [entry.name, entry.dmaCode, entry.regionName, entry.regionCode, entry.id]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(needle))
    );
  }, [entries, searchText]);

  useEffect(() => {
    setPage(1);
  }, [searchText, types]);

  const columns = [
    {
      title: 'Type',
      dataIndex: 'resourceType',
      key: 'resourceType',
      width: 100,
      render: (type) => <Tag color={TYPE_COLORS[type] || 'default'}>{(type || '').toUpperCase()}</Tag>,
    },
    {
      title: 'Nexxen ID',
      dataIndex: 'id',
      key: 'id',
      width: 110,
      render: (id) => (
        <Tooltip title="Paste this into the line item's geo targeting">
          <code>{id}</code>
        </Tooltip>
      ),
    },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'DMA Code',
      dataIndex: 'dmaCode',
      key: 'dmaCode',
      width: 110,
      render: (code) => code || <span style={{ color: '#bfbfbf' }}>&mdash;</span>,
    },
    {
      title: 'State',
      dataIndex: 'regionName',
      key: 'regionName',
      render: (name, record) => (record.regionCode ? `${name} (${record.regionCode})` : name || ''),
    },
  ];

  // ─── Toolbar: type filter, search, export, refresh (portaled into tab bar) ──
  const toolbarButtons = (
    <>
      <Select
        mode="multiple"
        value={types}
        onChange={(value) => setTypes(value.length ? value : ['REGION'])}
        style={{ minWidth: 200 }}
        maxTagCount="responsive"
      >
        <Option value="REGION">States</Option>
        <Option value="DMA">DMAs</Option>
        <Option value="CITY">Cities</Option>
      </Select>
      <div className="targeting-search">
        <Input
          placeholder="Search name, DMA code or state"
          value={searchText}
          allowClear
          prefix={<SearchOutlined />}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>
      {!isTablet && (
        <>
          <CSVLink
            filename={`nexxen-geo-library-${library ? library.countryCode.toLowerCase() : 'us'}.csv`}
            headers={CSV_HEADERS}
            data={filteredData}
            style={{ textDecoration: 'none' }}
          >
            <Button icon={<DownloadOutlined />} disabled={!filteredData.length}>
              Export CSV
            </Button>
          </CSVLink>
          {isAdmin && (
            <Tooltip title="Re-pull the taxonomy from Nexxen instead of the cached snapshot">
              <Button icon={<ReloadOutlined />} loading={loading} onClick={() => loadLibrary(true)}>
                Refresh
              </Button>
            </Tooltip>
          )}
        </>
      )}
    </>
  );

  const renderBody = () => {
    if (loading) return <Spin />;
    if (error) return <Alert type="error" message="Could not load the geo library" description={error.message} showIcon />;
    if (!filteredData.length) return <Empty />;

    return (
      <div className="targeting-region__table">
        <PaginationTable
          columns={columns}
          data={filteredData}
          scroll={{ x: 500 }}
          bordered={false}
          paginationSize={'default'}
          size={'small'}
          current={page}
          pageSize={pageSize}
          total={filteredData.length}
          onChange={(paginationObject) => {
            setPageSize(paginationObject.pageSize);
            setPage(paginationObject.current);
          }}
          rowKey={(record) => `${record.resourceType}-${record.id}`}
        />
      </div>
    );
  };

  return (
    <div className="targeting-region">
      {toolbarContainer && ReactDOM.createPortal(toolbarButtons, toolbarContainer)}

      {library && library.queryTotal > entries.length && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Showing the first ${entries.length} of ${library.queryTotal} matching entries`}
          description="Narrow the result with the search box or by selecting fewer types. The CSV export only contains the rows loaded here."
        />
      )}

      {library && (
        <div style={{ marginBottom: 12, color: '#8c8c8c', fontSize: 12 }}>
          {`${filteredData.length} of ${entries.length} entries · `}
          {`${library.counts.region} states, ${library.counts.dma} DMAs, ${library.counts.city} cities · `}
          {library.cached ? 'cached snapshot' : 'live from Nexxen'}
          {` · pulled ${new Date(library.generatedAt).toLocaleString()}`}
        </div>
      )}

      {renderBody()}
    </div>
  );
};

export default GeoLibrary;
